import {
  BodyText,
  Button,
  Callout,
  Field,
  FieldError,
  FieldLabel,
  HeaderText,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@/components/ui";
import { useEpisodesOfCareList } from "@/network/useEpisodesOfCare";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import type { ChangeEvent } from "react";
import { Controller, useForm } from "react-hook-form";
import { DEFAULT_CATEGORY, useFilesContext } from "../FilesContext";
import { useCreateFile } from "../network/useCreateFile";
import { useFileCategories } from "../network/useFileCategories";
import { type NewFileForm, newFileSchema } from "../schemas";
import { FileViewer } from "./FileViewer";

interface NewFileProps {
  onChange(e: ChangeEvent<HTMLInputElement>): void;
  onCancel(): void;
  onSave(): void;
  file?: File;
}

export const NewFile = ({ onCancel, onSave, file }: NewFileProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const { data: eocData } = useEpisodesOfCareList({ patientId });
  const { selectedCategory } = useFilesContext();
  const { data: categoriesData } = useFileCategories();
  const { mutateAsync, isPending } = useCreateFile(patientId);

  const form = useForm<NewFileForm>({
    resolver: zodResolver(newFileSchema),
    mode: "onSubmit",
    defaultValues: {
      category: selectedCategory === DEFAULT_CATEGORY ? "" : selectedCategory,
      eoc: "",
      file,
    },
  });

  const onSubmit = async (values: NewFileForm) => {
    try {
      await mutateAsync({
        patientId,
        categoryId: values.category,
        eocId: values.eoc,
        file: values.file,
      });
      if (
        selectedCategory !== DEFAULT_CATEGORY &&
        values.category !== selectedCategory
      ) {
        toast.success(
          "File added successfully, hidden due to current category filter",
        );
      } else {
        toast.success("File added successfully");
      }
      onSave();
    } catch (error) {
      toast.error("Failed to add file. Please try again.");
    }
  };

  return (
    <div className="w-full">
      <div className="">
        <HeaderText size="sm">Add New File</HeaderText>
      </div>

      <BodyText className="mt-2" size="sm">
        {file?.name}
      </BodyText>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="mt-3 flex gap-3">
          <Controller
            control={form.control}
            name="category"
            render={({ field, fieldState }) => (
              <Field
                className="w-full max-w-40"
                data-invalid={fieldState.invalid}
              >
                <FieldLabel htmlFor="category">Category</FieldLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="category"
                    aria-invalid={fieldState.invalid}
                  >
                    <SelectValue
                      placeholder="Select Category"
                      data-testid="selected-category"
                    />
                  </SelectTrigger>
                  <SelectContent data-testid="category-select-content">
                    <SelectGroup>
                      {categoriesData?.categories.map(({ id, type }) => (
                        <SelectItem key={id} value={id}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="eoc"
            render={({ field, fieldState }) => (
              <Field
                className="w-full max-w-40"
                data-invalid={fieldState.invalid}
              >
                <FieldLabel htmlFor="eoc">EOC</FieldLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="eoc" aria-invalid={fieldState.invalid}>
                    <SelectValue
                      placeholder="Select EOC"
                      data-testid="selected-eoc"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {eocData?.map(({ id, title }) => (
                        <SelectItem key={id} value={id}>
                          {title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>

        {file ? (
          <FileViewer existingFile={null} newFile={file} />
        ) : (
          <Callout
            title="Something went wrong"
            description="Please upload a new file"
            variant="danger"
            className="mt-3"
          />
        )}

        <div className="flex gap-3 mt-3 w-fit ml-auto">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" type="submit" isLoading={isPending}>
            Add File
          </Button>
        </div>
      </form>
    </div>
  );
};
